import { useMutation } from '@apollo/client';
import {
  ComponentProps, useContext, useEffect, useState,
} from 'react';
import { ActionContext } from '../../..';
import { WorkflowContext, WorkflowDataType } from '../workflowCompletion';
import { usePermissions, UserContext } from '../../../../providers/userContextProvider';
import { InnerWorkflowModal } from '../../../3-pattern/innerWorkflowModal/innerWorkflowModal';
import { CREATE_WORKFLOW_COMPLETION, UPDATE_WORKFLOW_COMPLETION } from '../subSteps/queries';
import { WorkflowCompletion } from '../../../../interfaces';
import { useClientContext } from '../../../../pages/client';
import { useHouseholdContext } from '../../../../pages/household';

type WithId<T> = T & { id: string };
export type WorkflowCompletionWithId = WithId<WorkflowCompletion>;

export const useInnerWorkflow = ({ workflowCompletion, refetch }: { workflowCompletion: WorkflowCompletionWithId, refetch: any }) => {
  const { workflowData } = useContext(WorkflowContext);
  const { refetch: refetchWorkflow } = useContext(ActionContext);
  const { permissions } = usePermissions();
  const [innerOpen, setInnerOpen] = useState(false);
  const [newActiveWorkflow, setNewActiveWorkflow] = useState<WorkflowCompletion>();
  const [updateWorkflowCompletion] = useMutation(UPDATE_WORKFLOW_COMPLETION(permissions));
  const [createWorkflowCompletionMutation] = useMutation(CREATE_WORKFLOW_COMPLETION(permissions));

  const clientContext = useClientContext();
  const { activeOrganization } = useContext(UserContext);
  const householdContext = useHouseholdContext();
  const organizationId = clientContext?.orgSettings.id || householdContext?.orgSettings.id || activeOrganization?.id;

  const updateParentWorkflowCompletion = async (parentWorkflowComplationId: string, updatedContext: any) => {
    await updateWorkflowCompletion({
      variables: {
        input: {
          workflowCompletionId: parentWorkflowComplationId,
          context: updatedContext ?? undefined,
        },
      },
    });
  };
  const createWorkflowCompletion = async (
    options: { workflowId: string, currentUserId?: string, workflowContext?: WorkflowDataType },
  ) => {
    const result = await createWorkflowCompletionMutation({
      variables: {
        input: {
          objectId: options.currentUserId,
          objectType: 'USER',
          workflowId: options.workflowId,
          organizationId,
          parentWorkflowCompletionId: workflowCompletion.id,
        },
      },
    });
    const { data } = result;
    await refetchWorkflow();
    setNewActiveWorkflow({
      ...data.createWorkflowCompletion.workflowCompletion,
      context: options?.workflowContext || workflowData,
    });
    setInnerOpen(true);
    if (workflowCompletion?.id) {
      const updatingContext = {
        ...workflowData,
        childWorkflowCompletion: data.createWorkflowCompletion.workflowCompletion,
      };
      await updateParentWorkflowCompletion(workflowCompletion.id, updatingContext);
    }
    return result;
  };

  const onUpdateParent: ComponentProps<typeof InnerWorkflowModal>['updateParent'] = (activeCompletion?: any) => {
    if (!activeCompletion) {
      delete workflowData.childWorkflowCompletion;
      updateParentWorkflowCompletion(workflowCompletion.id, {
        ...workflowData,
      });
    } else {
      const updatingContext = {
        ...workflowData,
        childWorkflowCompletion: activeCompletion,
      };
      updateParentWorkflowCompletion(workflowCompletion.id, updatingContext);
    }
  };

  const onRefetch: ComponentProps<typeof InnerWorkflowModal>['refetch'] = async () => {
    await Promise.all([
      refetchWorkflow(),
      refetch(),
    ]);
  };

  useEffect(() => {
    if (workflowCompletion && workflowCompletion?.context?.childWorkflowCompletion) {
      setNewActiveWorkflow({
        ...workflowCompletion?.context?.childWorkflowCompletion,
        context: workflowData,
      });
    }
  }, [workflowCompletion, workflowData]);

  return {
    createWorkflowCompletion,
    innerWorkflowModal: newActiveWorkflow && (
      <InnerWorkflowModal
        newActiveWorkflow={newActiveWorkflow}
        setOpen={setInnerOpen}
        open={innerOpen}
        refetch={onRefetch}
        updateParent={onUpdateParent}
      />
    ),
  };
};
